import React from "react";
import moment from "moment";
import { Line } from "react-chartjs-2";
import LoadingAnimation from "../../../../components/LoadingAnimation";
interface SingleServiceDetailViewProps {
  selectedDate: moment.Moment;
  setSelectedDate: (date: moment.Moment) => void;
  selectorDates: moment.Moment[];
  labels: string[];
  chartData: any;
  options: any;
  renderAlarmFailureDetails: () => JSX.Element[];
  renderAlarmFailureDetailsCount: () => any[] | undefined;
  renderTestFailureDetails: () => JSX.Element[];
  renderTestFailureDetailsCount: () => any[] | undefined;
  handleBackToAllServices: () => void;
  isLoadingServiceHealthHistory: boolean;
  isLoadingServiceHealthHistoryByBrand: boolean;
  isLoadingTestsExecutions: boolean;
  selectedBrands: any[];
  fetchPlatformHealthServiceDetail: (date: moment.Moment) => void;
}

export default function SingleServiceDetailView({
  selectedDate,
  setSelectedDate,
  selectorDates,
  labels,
  chartData,
  options,
  selectedBrands,
  renderAlarmFailureDetails,
  renderAlarmFailureDetailsCount,
  renderTestFailureDetails,
  renderTestFailureDetailsCount,
  handleBackToAllServices,
  isLoadingServiceHealthHistory,
  isLoadingServiceHealthHistoryByBrand,
  isLoadingTestsExecutions,
  fetchPlatformHealthServiceDetail,
}: SingleServiceDetailViewProps) {
  const alarmsLoading = !renderAlarmFailureDetailsCount();
  const testsLoading =
    !renderTestFailureDetailsCount() || isLoadingTestsExecutions;
  const chartLoading =
    selectedBrands.length === 0
      ? isLoadingServiceHealthHistory
      : isLoadingServiceHealthHistoryByBrand;

  // Add an onClick handler to the chart to synchronize clicks with the dropdown
  options.onClick = (event: any, elements: string | any[]) => {
    if (elements.length > 0) {
      const index = elements[0].index;
      const selectedLabel = labels[index];
      const selectedValue = selectorDatesDict[selectedLabel]; // Find the value in the dictionary
      if (selectedValue) {
        const newDate = moment(selectedValue, "DD-MM-YYYY HH:mm:ss");
        console.log("elements newDate", newDate);
        setSelectedDate(newDate);
        fetchPlatformHealthServiceDetail(newDate); // Call the parent method
      }
    }
  };

  // Update the point radius dynamically to highlight the selected date
  chartData.datasets = [
    {
      ...chartData.datasets[0],
      pointRadius: labels.map((label) =>
        moment(label, "DD-MM-YYYY HH:mm").isSame(selectedDate, "day") ? 10 : 6
      ),
    },
  ];

  // Transform selectorDates into a dictionary with labels and real values
  const selectorDatesDict = selectorDates.reduce(
    (acc: { [key: string]: string }, date) => {
      const label = date.format("DD-MM-YYYY");
      const value = date.format("DD-MM-YYYY HH:mm:ss");
      acc[label] = value;
      return acc;
    },
    {}
  );

  const selectedValue = Object.values(selectorDatesDict)?.find((value) =>
    moment(value, "DD-MM-YYYY").isSame(selectedDate, "day")
  );

  return (
    <>
      <div className="mt-4">
        <button
          onClick={handleBackToAllServices}
          className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-sm"
        >
          Back to all services
        </button>
      </div>

      <>
        {!chartLoading && (
          <div className="flex items-center mt-4 gap-2">
            <div>
              <p className="text-sm font-medium text-gray-700">Health on</p>
            </div>
            <select
              value={selectedValue}
              onChange={(e) => {
                const value = e.target.value;
                const dateValue = value
                  ? moment(value, "DD-MM-YYYY HH:mm:ss")
                  : moment();
                setSelectedDate(dateValue);
              }}
              className="block border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
            >
              {Object.entries(selectorDatesDict).map(([, value]) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {alarmsLoading && <LoadingAnimation title={`Loading alarms`} />}
            {!alarmsLoading &&
              (renderAlarmFailureDetailsCount() || []).length > 0 && (
                <div className="col-span-1">
                  <div className="mt-8">
                    <div className="mb-4">
                      <h2 className="text-2xl font-bold text-gray-800">
                        Alarms
                      </h2>
                    </div>
                    {renderAlarmFailureDetails()}
                  </div>
                </div>
              )}

            {testsLoading && <LoadingAnimation title={`Loading tests`} />}
            {!testsLoading &&
              (renderTestFailureDetailsCount() || []).length > 0 && (
                <div className="col-span-1">
                  <div className="mt-8">
                    <div className="mb-4">
                      <h2 className="text-2xl font-bold text-gray-800">
                        Tests
                      </h2>
                    </div>
                    {renderTestFailureDetails()}
                  </div>
                </div>
              )}
          </div>
        </div>

        {chartLoading && (
          <div className="flex justify-center items-center h-[200px]">
            <LoadingAnimation title={`Loading chart history`} />
          </div>
        )}

        {!chartLoading && (
          <>
            <div className="mb-4 mt-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
              <h2 className="text-2xl font-bold text-gray-800">
                Health Evolution
              </h2>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <p>From {labels?.[0] || ""}</p>
                <p>To {labels?.[labels.length - 1] || ""}</p>
              </div>
            </div>

            <div className="w-full h-[200px] relative">
              <Line data={chartData} options={options} />
            </div>
          </>
        )}
      </>
    </>
  );
}
